import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { IconButton, Typography } from '@mui/material';
import { makeStyles } from '@mui/styles';
import { TFunction } from 'i18next';
import { FC } from 'react';
import { formatMoney } from 'utils/helpers';
import { AccountingInvolvedParty } from 'v2-types/order';

import { LabeledValue } from './labeledValue';

const useStyles = makeStyles((theme: any) => ({
  list: {
    display: 'flex',
    flexDirection: 'column',
    gap: theme.spacing(1),
    minWidth: 0,
  },
  party: {
    display: 'flex',
    flexDirection: 'column',
    gap: theme.spacing(1),
    minWidth: 0,
    padding: theme.spacing(1.5),
    border: `1px solid ${theme.palette.button.default}`,
    borderRadius: theme.shape.borderRadius,
  },
  header: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    gap: theme.spacing(1),
    minWidth: 0,
  },
  name: {
    flex: 1,
    minWidth: 0,
    color: theme.palette.primary.darker,
    fontWeight: theme.typography.fontWeightMedium,
    textTransform: 'capitalize',
  },
  actions: {
    display: 'flex',
    flexShrink: 0,
    alignItems: 'center',
    '& button': {
      minWidth: theme.spacing(5.5),
      minHeight: theme.spacing(5.5),
    },
  },
  edit: {
    color: theme.palette.success.main,
  },
  trash: {
    color: theme.palette.error.main,
  },
  fields: {
    display: 'grid',
    gridTemplateColumns: 'repeat(2, minmax(0, 1fr))',
    gap: theme.spacing(1.5),
  },
  capitalized: {
    textTransform: 'capitalize',
  },
  empty: {
    fontWeight: theme.typography.fontWeightMedium,
    color: theme.palette.tab.offselected,
  },
}));

type InvolvedPartiesListProps = Readonly<{
  parties: readonly AccountingInvolvedParty[];
  onEditParty: (party: AccountingInvolvedParty) => void;
  onDeleteParty: (party: AccountingInvolvedParty) => void;
  t: TFunction;
}>;

export const InvolvedPartiesList: FC<InvolvedPartiesListProps> = ({
  parties,
  onEditParty,
  onDeleteParty,
  t,
}) => {
  const classes = useStyles();

  if (parties.length === 0) {
    return (
      <Typography variant="body2" className={classes.empty}>
        {t('contact:no-items')}
      </Typography>
    );
  }

  return (
    <div className={classes.list}>
      {parties.map((party) => (
        <div className={classes.party} key={`${party.kind}-${party._id}`}>
          <div className={classes.header}>
            <Typography variant="body2" className={classes.name}>
              {party.name}
            </Typography>
            <div className={classes.actions}>
              <IconButton
                className={classes.edit}
                aria-label={`${t('disbursments:edit')} ${party.name}`}
                onClick={() => onEditParty(party)}
                size="large"
              >
                <FontAwesomeIcon icon="edit" size="sm" />
              </IconButton>
              <IconButton
                className={classes.trash}
                aria-label={`${t('disbursments:delete')} ${party.name}`}
                onClick={() => onDeleteParty(party)}
                size="large"
              >
                <FontAwesomeIcon icon="trash" size="sm" />
              </IconButton>
            </div>
          </div>
          <div className={classes.fields}>
            <LabeledValue label={t('accounting:kind')}>
              <Typography variant="body2" className={classes.capitalized}>
                {party.kind}
              </Typography>
            </LabeledValue>
            <LabeledValue label={t('disbursments:amount')}>
              <Typography variant="body2">{formatMoney(party.amount)}</Typography>
            </LabeledValue>
          </div>
        </div>
      ))}
    </div>
  );
};
